import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users, Armchair, ChevronDown, ChevronRight, Lock, Clock3 } from 'lucide-react';
import { api, parseApiError, type RosterOverviewClan, type RosterOverviewSeat } from '../lib/api';
import { Card } from './Card';
import { PageSpinner } from './Spinner';
import { HelpText } from './HelpText';
import { cn } from '../lib/cn';

// Setup → Seats-step roster overview (Lisa, 2026-07-09): "so admin and
// helper can use this to see an easy way to see WHO IS IN WHICH TEAM" — a
// comp-wide, READ-ONLY glance across every enrolled clan's roster. Editing
// stays in the per-clan workbench (Clans step) — this pane only links out
// to it via "Manage seats".
//
// House doctrine followed here (see memory: no severity-colour highlight
// strips, GG Anvil visual restraint): seat/status state is conveyed by
// TYPOGRAPHY (weight/case/muted vs primary ink), never by colour-coding —
// no red/amber/emerald anywhere in this panel.

function statusLabel(status: RosterOverviewSeat['status']): string {
  if (status === 'CLAIMED') return 'Claimed';
  if (status === 'PENDING_CLAIM') return 'Pending';
  return 'Ghost';
}

// One seat row — gamertag, resolved player name when claimed, quiet status.
// Its own component so a future per-row affordance (e.g. a hover action)
// gets its own hook scope without complicating the card/group above it.
function SeatGlanceRow({ seat }: { seat: RosterOverviewSeat }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.04] px-3 py-1.5">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate font-mono text-xs text-ink-primary">{seat.gamertag}</span>
        {seat.playerName && seat.playerName !== seat.gamertag && (
          <span className="truncate text-[11px] text-ink-meta">{seat.playerName}</span>
        )}
      </div>
      <span
        className={cn(
          'shrink-0 font-display text-[9px] font-black uppercase tracking-[0.18em]',
          seat.status === 'CLAIMED' ? 'text-ink-secondary' : 'text-ink-meta',
        )}
      >
        {statusLabel(seat.status)}
      </span>
    </div>
  );
}

// Groups a clan's flat seat list into team buckets for heading display —
// named/numbered teams (sorted low→high), THEN "Unassigned" last. Pulled out
// so ClanRosterCard's own useMemo stays a one-line call.
function groupSeatsByTeam(seats: RosterOverviewSeat[]): Array<{ key: string; label: string; teamNo: number | null; seats: RosterOverviewSeat[] }> {
  const byKey = new Map<string, { key: string; label: string; teamNo: number | null; seats: RosterOverviewSeat[] }>();
  for (const seat of seats) {
    const key = seat.teamNo == null ? 'unassigned' : `team-${seat.teamNo}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: seat.teamNo == null ? 'Unassigned' : (seat.teamName ?? `Team ${seat.teamNo}`),
        teamNo: seat.teamNo,
        seats: [],
      });
    }
    byKey.get(key)!.seats.push(seat);
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.teamNo == null) return 1;
    if (b.teamNo == null) return -1;
    return a.teamNo - b.teamNo;
  });
}

// Expandable per-clan card — mirrors ClanWorkbench's ClanCard idiom (header
// row = name + quiet count chips + chevron; body only mounts once expanded).
function ClanRosterCard({ clan, compId }: { clan: RosterOverviewClan; compId: string }) {
  const [expanded, setExpanded] = useState(false);
  const unclaimed = clan.counts.total - clan.counts.claimed;
  const groups = useMemo(() => groupSeatsByTeam(clan.seats), [clan.seats]);

  return (
    <Card className="!p-4">
      <button type="button" onClick={() => setExpanded(o => !o)} className="flex w-full items-center justify-between gap-3 text-left">
        <div className="flex min-w-0 items-center gap-2">
          <Users className="h-4 w-4 shrink-0 text-ink-meta" />
          <span className="truncate font-display text-sm font-bold text-ink-primary">{clan.clanName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-meta">
            <span className="font-bold text-ink-primary">{clan.counts.claimed} claimed</span>
            {clan.counts.pending > 0 && <span>{clan.counts.pending} pending</span>}
            {clan.counts.ghost > 0 && <span>{clan.counts.ghost} ghost</span>}
          </div>
          {expanded ? <ChevronDown className="h-4 w-4 text-ink-meta" /> : <ChevronRight className="h-4 w-4 text-ink-meta" />}
        </div>
      </button>

      {/* Surfaced plainly whether expanded or not — no need to open the card to see there's work to do. */}
      {clan.counts.total > 0 && unclaimed > 0 && (
        <p className="mt-2 text-xs font-medium text-ink-secondary">{unclaimed} unclaimed seat{unclaimed === 1 ? '' : 's'}</p>
      )}

      {expanded && (
        <div className="mt-4 space-y-4">
          {clan.counts.total === 0 ? (
            <p className="text-xs text-ink-meta">No seats yet for this clan — nothing to see here until players are added.</p>
          ) : (
            groups.map(group => (
              <div key={group.key}>
                <p className="mb-1.5 font-display text-[10px] font-black uppercase tracking-[0.22em] text-forge-300">
                  {group.label}
                </p>
                <div className="space-y-1.5">
                  {group.seats.map(seat => <SeatGlanceRow key={seat.gamertag} seat={seat} />)}
                </div>
              </div>
            ))
          )}
          <Link
            to={`/admin/clans?compId=${encodeURIComponent(compId)}&clan=${encodeURIComponent(clan.clanName)}`}
            className="btn-ghost inline-flex items-center gap-1.5 text-xs"
          >
            <Armchair className="h-3.5 w-3.5" /> Manage seats <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </Card>
  );
}

export function RosterOverviewPanel({ compId }: { compId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['rosterOverview', compId],
    queryFn: () => api.rosterOverview(compId),
    enabled: !!compId,
  });

  if (!compId) {
    return <p className="text-sm text-ink-meta">Pick a competition above to view its roster.</p>;
  }
  if (isLoading) return <PageSpinner />;
  if (error) {
    return <p className="text-sm text-red-400">{parseApiError(error).message}</p>;
  }
  if (!data) return null;

  const { rollup } = data;

  return (
    <div className="space-y-4">
      <HelpText>
        <p className="mb-1 text-xs text-ink-meta">
          A read-only glance across every enrolled clan's roster — who's claimed, who's still a ghost seat,
          and which team they're on. Edit rosters from each clan's own workbench via <b>Manage seats</b> below.
        </p>
      </HelpText>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="font-display text-sm font-bold text-ink-primary">
          {rollup.teams} team{rollup.teams === 1 ? '' : 's'} · {rollup.totalSeats} seat{rollup.totalSeats === 1 ? '' : 's'} · {rollup.claimed} claimed · {rollup.unclaimed} unclaimed
        </p>
      </div>

      {data.startDate && data.daysUntilLock != null && (
        <p className="flex items-center gap-1.5 text-xs text-ink-secondary">
          <Clock3 className="h-3.5 w-3.5 shrink-0" />
          Rosters lock {data.startDate} ({data.daysUntilLock} day{data.daysUntilLock === 1 ? '' : 's'})
        </p>
      )}
      {data.startDate && data.rosterLocked && (
        <p className="flex items-center gap-1.5 text-xs text-ink-secondary">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Rosters locked {data.startDate}
        </p>
      )}

      {data.clans.length === 0 ? (
        <p className="text-sm text-ink-meta">No clans enrolled yet — enroll clans from the Clans step first.</p>
      ) : (
        <div className="space-y-2">
          {data.clans.map(clan => (
            <ClanRosterCard key={clan.clanId} clan={clan} compId={compId} />
          ))}
        </div>
      )}
    </div>
  );
}
